import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useHunt, useStartHunt } from '../hooks/useHunt'
import { useAuth } from '../hooks/useAuth'
import { Button } from '../components/common/Button'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { submitReport } from '../services/api'
import type { HuntDifficulty } from '../types'

const REPORT_CATEGORIES = [
  { value: 'wrong_location', label: 'Wrong location' },
  { value: 'unsafe', label: 'Unsafe area' },
  { value: 'clue_error', label: 'Clue error' },
  { value: 'offensive_content', label: 'Offensive content' },
  { value: 'other', label: 'Other' },
]

function ReportModal({ huntId, onClose }: { huntId: string; onClose: () => void }) {
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!category) return
    setLoading(true)
    setError('')
    try {
      await submitReport(huntId, { category, description: description || undefined })
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-brown-900 mb-4">Report a problem</h3>
        {success ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-brown-700 mb-4">Thanks for your report! We'll review it shortly.</p>
            <Button onClick={onClose}>Close</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-2">What's the issue?</label>
              <div className="space-y-2">
                {REPORT_CATEGORIES.map((cat) => (
                  <label key={cat.value} className="flex items-center gap-3 p-3 rounded-lg border border-brown-200 cursor-pointer hover:bg-cream-50">
                    <input
                      type="radio"
                      name="category"
                      value={cat.value}
                      checked={category === cat.value}
                      onChange={(e) => setCategory(e.target.value)}
                      className="accent-primary-600"
                    />
                    <span className="text-sm text-brown-800">{cat.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-1">Details (optional)</label>
              <textarea
                className="w-full border border-brown-200 rounded-lg p-3 text-sm text-brown-800 resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"
                rows={3}
                placeholder="Describe the issue..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-3">
              <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button type="submit" className="flex-1" loading={loading} disabled={!category}>Submit</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// Difficulty badge component matching Figma design
function DifficultyBadge({ difficulty }: { difficulty: HuntDifficulty }) {
  const config = {
    easy: { emoji: '🌿', bg: 'bg-green-100', text: 'text-green-700', label: 'Easy' },
    medium: { emoji: '⚡', bg: 'bg-primary-100', text: 'text-primary-700', label: 'Medium' },
    hard: { emoji: '🔥', bg: 'bg-red-100', text: 'text-red-700', label: 'Hard' },
  }
  const { emoji, bg, text, label } = config[difficulty]

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${bg} ${text}`}>
      <span>{emoji}</span>
      {label}
    </span>
  )
}

export default function HuntDetail() {
  const { huntId } = useParams<{ huntId: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { data: hunt, isLoading, error } = useHunt(huntId!)
  const startHuntMutation = useStartHunt()
  const [showReportModal, setShowReportModal] = useState(false)

  const handleStartHunt = async () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    try {
      await startHuntMutation.mutateAsync(huntId!)
      navigate(`/hunts/${huntId}/play`)
    } catch (err) {
      console.error('Failed to start hunt:', err)
    }
  }

  if (isLoading) {
    return <LoadingSpinner className="min-h-screen" />
  }

  if (error || !hunt) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4">
        <Card padding="lg" className="text-center">
          <h2 className="text-xl font-semibold text-brown-900 mb-2">Hunt not found</h2>
          <p className="text-brown-600 mb-4">This hunt doesn't exist or is no longer available.</p>
          <Link to="/hunts">
            <Button>Browse Hunts</Button>
          </Link>
        </Card>
      </div>
    )
  }

  const hasStarted = !!hunt.user_progress
  const isComplete = hunt.user_progress?.status === 'completed'

  return (
    <div className="min-h-screen bg-cream-50">
      {/* Header with back button and difficulty badge */}
      <header className="absolute top-0 left-0 right-0 z-10 px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Link
            to="/hunts"
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md"
          >
            <svg className="w-5 h-5 text-brown-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <DifficultyBadge difficulty={hunt.difficulty} />
        </div>
      </header>

      {/* Cover image */}
      <div className="relative h-56">
        {hunt.cover_image_url ? (
          <img
            src={hunt.cover_image_url}
            alt={hunt.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary-300 to-primary-500" />
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-cream-50 via-transparent to-transparent" />

      </div>

      {/* Content */}
      <main className="max-w-lg mx-auto px-4 -mt-4 pb-8 space-y-6 relative z-10">
        {/* Title and creator */}
        <div>
          <h1 className="text-2xl font-bold text-brown-900 mb-2">{hunt.title}</h1>
          {hunt.creator_name && (
            <div className="flex items-center gap-2 text-brown-600">
              <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-xs">👤</span>
              </div>
              <span className="text-sm">Quest by <span className="font-medium text-primary-700">{hunt.creator_name}</span></span>
            </div>
          )}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-3">
          <Card padding="md" className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span className="text-xs text-brown-500">Waypoints</span>
            </div>
            <p className="text-xl font-bold text-brown-900">{hunt.waypoint_count}</p>
            <p className="text-xs text-brown-500">stops</p>
          </Card>

          <Card padding="md" className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-brown-500">Duration</span>
            </div>
            <p className="text-xl font-bold text-brown-900">
              {hunt.estimated_duration_minutes ? `~${hunt.estimated_duration_minutes}` : '?'}
            </p>
            <p className="text-xs text-brown-500">min</p>
          </Card>

          <Card padding="md" className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <span className="text-xs text-brown-500">Distance</span>
            </div>
            <p className="text-xl font-bold text-brown-900">
              {hunt.total_distance_meters
                ? (hunt.total_distance_meters / 1000).toFixed(1)
                : '?'}
            </p>
            <p className="text-xs text-brown-500">km</p>
          </Card>

          <Card padding="md" className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-xs text-brown-500">Adventurers</span>
            </div>
            <p className="text-xl font-bold text-brown-900">{hunt.total_starts}</p>
          </Card>
        </div>

        {/* About section */}
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-brown-900 mb-3">
            <span>📜</span>
            About This Quest
          </h2>
          <p className="text-brown-700 leading-relaxed">{hunt.description}</p>
        </div>

        {/* Sponsor */}
        {hunt.sponsor && (
          <Card padding="md" variant="parchment">
            <div className="flex items-start gap-3">
              {hunt.sponsor.business_logo_url ? (
                <img
                  src={hunt.sponsor.business_logo_url}
                  alt={hunt.sponsor.business_name}
                  className="h-12 w-12 rounded-lg object-cover"
                />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-xl">
                  🏷️
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-wide text-brown-500">
                  Sponsored by
                </p>
                <p className="font-semibold text-brown-900">
                  {hunt.sponsor.business_name}
                </p>
                {hunt.sponsor.business_description && (
                  <p className="mt-1 text-sm text-brown-700">
                    {hunt.sponsor.business_description}
                  </p>
                )}
                {hunt.sponsor.business_website && (
                  <a
                    href={hunt.sponsor.business_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-block text-sm text-primary-700 underline"
                  >
                    Visit website →
                  </a>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Featured reward / first-N pool */}
        {hunt.featured_reward && (() => {
          const fr = hunt.featured_reward
          const isPool = fr.total_quantity != null
          const remaining = fr.remaining ?? null
          const exhausted = isPool && (remaining ?? 0) <= 0
          return (
            <Card padding="md" variant="parchment">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-100 text-2xl">
                  🎁
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-wide text-brown-500">
                    Reward
                  </p>
                  <p className="font-semibold text-brown-900">{fr.name}</p>
                  {fr.description && (
                    <p className="mt-1 text-sm text-brown-700">{fr.description}</p>
                  )}
                  {isPool && (
                    <p
                      className={`mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        exhausted
                          ? 'bg-stone-200 text-stone-600'
                          : 'bg-amber-200 text-amber-900'
                      }`}
                    >
                      {exhausted
                        ? 'Pool claimed'
                        : `${remaining} of ${fr.total_quantity} left — first ${fr.total_quantity} finishers!`}
                    </p>
                  )}
                  {exhausted && (
                    <Link
                      to="/hunts"
                      className="mt-2 inline-block text-sm font-medium text-primary-700 underline"
                    >
                      Browse other hunts with rewards →
                    </Link>
                  )}
                </div>
              </div>
            </Card>
          )
        })()}

        {/* Progress (if started) */}
        {hasStarted && !isComplete && (
          <Card padding="md" variant="parchment">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <span className="flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-primary-500"></span>
                  </span>
                </div>
                <div>
                  <p className="font-medium text-brown-900">Hunt in progress</p>
                  <p className="text-sm text-brown-600">
                    {hunt.user_progress!.completed_waypoints.length} of {hunt.waypoint_count} waypoints
                  </p>
                </div>
              </div>
              <Link to={`/hunts/${huntId}/play`}>
                <Button size="sm">Continue</Button>
              </Link>
            </div>
          </Card>
        )}

        {isComplete && (
          <Card padding="md" className="bg-green-50 border-green-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-brown-900">Hunt completed!</p>
                <p className="text-sm text-brown-600">Great job finishing this quest.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Social proof */}
        {hunt.total_completions > 0 && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-primary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-brown-600">
              <span className="font-semibold text-brown-900">{hunt.total_completions}</span>{' '}
              {hunt.total_completions === 1 ? 'adventurer has' : 'adventurers have'} completed this hunt
            </p>
          </div>
        )}

        {/* Start button */}
        {!hasStarted && (
          <Button
            className="w-full"
            size="lg"
            onClick={handleStartHunt}
            loading={startHuntMutation.isPending}
          >
            <span className="flex items-center gap-2">
              <span>✨</span>
              Begin Adventure
            </span>
          </Button>
        )}

        {/* Report link */}
        {isAuthenticated && (
          <div className="text-center">
            <button
              onClick={() => setShowReportModal(true)}
              className="text-sm text-brown-400 hover:text-brown-600 underline underline-offset-2"
            >
              Report a problem with this hunt
            </button>
          </div>
        )}
      </main>

      {showReportModal && (
        <ReportModal huntId={huntId!} onClose={() => setShowReportModal(false)} />
      )}
    </div>
  )
}
